import { UploadStage } from "@/components/dsd-view/upload-progress-inline";
import { AnimatePresence, motion } from "framer-motion";
import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

// 기존 stages 객체 (하위호환성 유지)
export const stages = {
  [UploadStage.CHUNKING]:
    "업로드한 DSD 파일을 분석하여 구조 정보를 추출 중입니다.",
  [UploadStage.CATEGORIZING]: "주석 목차 단위로 테이블을 분할합니다.",
  [UploadStage.TABLE_PARSING]: "표 형식과 구조를 AI가 분석 중입니다.",
  [UploadStage.TABLE_PROCESSING]:
    "1,800개 공시 사례와 비교하여 XBRL 태그 구조를 검증합니다.",
  [UploadStage.ROLE_ASSIGNING]: "AI가 알맞은 주석 목차를 배정 중입니다.",
  [UploadStage.TEXT_BLOCK]:
    "문장 주석 영역을 감지하고 공시 필수 여부를 판단 합니다.",
  [UploadStage.FINAL_STAGE]: "분석이 곧 완료됩니다.",
  [UploadStage.COMPLETE]: "DSD 보고서 분석이 완료되었습니다.",
  default: "처리 중...",
};

// 기존 AnimatedText 컴포넌트 (하위호환성 유지)
export const AnimatedText = ({ message }: { message: string }) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={message}
        className="flex py-4 flex-col items-center gap-[3.2px] self-stretch"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <span className="heading-1-semibold text-primary">{message}</span>
      </motion.div>
    </AnimatePresence>
  );
};

// 기존 ProgressBar 컴포넌트 (하위호환성 유지)
export const ProgressBar = ({ progress }: { progress: number }) => {
  return (
    <div className="w-full h-[5px] bg-white-200 rounded-full overflow-hidden relative">
      <div
        className="h-full bg-blue-600 transition-all duration-300 ease-out relative"
        style={{ width: `${Math.min(progress, 100)}%` }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "repeating-linear-gradient(-45deg, #93C5FD, #93C5FD 3.5px, transparent 3.5px, transparent 7px)",
            animation: "moveStripes 3s linear infinite",
          }}
        />
      </div>
      <style>{`
          @keyframes moveStripes {
            from { background-position: 0 0; }
            to   { background-position: 28px 0; }
          }
        `}</style>
    </div>
  );
};

// 분석 중 상태에서 보여줄 메시지와 프로그레스 바 컴포넌트 (신규)
export const AnalyzingContent = ({ message }: { message: string }) => {
  return (
    <div className="flex flex-col items-center gap-3 w-[256px]">
      <div className="flex flex-col items-center gap-[2px]">
        <AnimatePresence mode="wait">
          <motion.p
            key={message}
            className="body-2-normal-medium text-primary text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {message}
          </motion.p>
        </AnimatePresence>
        <p className="label-2-medium text-neutral-500 text-center">
          분석이 완료된 다른 주석 목차를 먼저 작업하세요!
        </p>
      </div>
      <StripedProgressBar />
    </div>
  );
};

// 스트라이프 애니메이션 프로그레스 바 (신규)
export const StripedProgressBar = () => {
  return (
    <div className="w-full h-[6px] bg-blue-300 rounded-full overflow-hidden relative">
      <div
        className="absolute inset-0"
        style={{
          background:
            "repeating-linear-gradient(-45deg, #2563eb, #2563eb 3px, transparent 3px, transparent 6px)",
          backgroundSize: "8.5px 100%",
          animation: "moveStripesNew 0.5s linear infinite",
        }}
      />
      <style>{`
        @keyframes moveStripesNew {
          from { background-position: 0 0; }
          to { background-position: 8.5px 0; }
        }
      `}</style>
    </div>
  );
};

// stages에서 "default", "final-stage", "complete" 제외한 메시지 배열
const excludeStages = [
  "default",
  UploadStage.FINAL_STAGE,
  UploadStage.COMPLETE,
];
const stageMessages = Object.entries(stages)
  .filter(([key]) => !excludeStages.includes(key))
  .map(([, message]) => message);

// RUNNING 상태: 5초마다 메시지 순환
export const AnalyzingState = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % stageMessages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <AnalyzingContent message={stageMessages[messageIndex]} />
    </div>
  );
};

/** 같은 패널 같은 자리에 번갈아 뜨는 안내 화면의 껍데기. 두 상태가 시각적 무게를
 *  공유해야 해서 한 곳에 둔다 — 여기만 고치면 둘 다 따라온다. */
const ActionableNotice = ({
  title,
  description,
  actionLabel,
  pendingLabel,
  icon,
  onAction,
  disabled = false,
  pending = false,
}: {
  title: string;
  description: string;
  actionLabel: string;
  pendingLabel: string;
  icon?: React.ReactNode;
  onAction: () => void;
  disabled?: boolean;
  pending?: boolean;
}) => {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 w-[256px]">
        <div className="flex flex-col items-center gap-[2px]">
          <p className="body-2-normal-medium text-primary text-center">
            {title}
          </p>
          <p className="label-2-medium text-neutral-500 text-center">
            {description}
          </p>
        </div>
        <button
          onClick={onAction}
          disabled={disabled || pending}
          aria-busy={pending}
          className="flex h-9 px-[10px] items-center justify-center gap-1 rounded-lg border border-neutral-200 hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-52 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:bg-transparent"
        >
          {!pending && icon}
          <span className="body-2-normal-medium text-primary">
            {pending ? pendingLabel : actionLabel}
          </span>
        </button>
      </div>
    </div>
  );
};

// FAILED 상태: 실패 메시지와 재시도 버튼
export const FailedState = ({
  onRetry,
  disabled = false,
  pending = false,
}: {
  onRetry: () => void;
  disabled?: boolean;
  pending?: boolean;
}) => {
  return (
    <ActionableNotice
      title="분석에 실패 했습니다."
      description="아래 버튼을 눌러 다시 시도해주세요."
      actionLabel="재시도"
      pendingLabel="분석 중…"
      icon={<RefreshCw className="w-[18px] h-[18px] text-primary" />}
      onAction={onRetry}
      disabled={disabled}
      pending={pending}
    />
  );
};

// NOT_TARGET 상태: 분석하지 않은 이유와 직접 분석 버튼
export const NotTargetState = ({
  onAnalyze,
  disabled = false,
  pending = false,
}: {
  onAnalyze: () => void;
  disabled?: boolean;
  pending?: boolean;
}) => {
  return (
    <ActionableNotice
      title="블록태깅 대상 주석이에요"
      description="필요하면 직접 분석할 수 있어요."
      actionLabel="분석하기"
      pendingLabel="분석 중…"
      onAction={onAnalyze}
      disabled={disabled}
      pending={pending}
    />
  );
};
